import {CodeViewHeader} from '@github-ui/code-view-header'
import type {HeaderResponse} from '@github-ui/code-view-header/types'
import type {LayoutPayload} from '@github-ui/code-view-types'
import {SplitPageLayout} from '@primer/react'

import {SharedPageLayout} from '../components/SharedPageLayout'
import {Nothing} from '../components/states/empty/Nothing'
import {OnboardingPrompts} from '../components/states/empty/OnboardingPrompts'
import {QuickSetupBox} from '../components/states/empty/QuickSetupBox'

// TODO: replace with a real `/_about` fetch once the empty-repo endpoint is wired up.
function buildMockHeaderData(data: LayoutPayload): HeaderResponse {
  const {repo, currentUser} = data
  return {
    ownerLogin: repo.ownerLogin,
    repoName: repo.name,
    isOrg: repo.isOrgOwned,
    topics: [],
    showInsights: false,
    stargazerCount: 0,
    watcherCount: 0,
    forksCount: 0,
    stargazersPath: '',
    watchersPath: '',
    forkNetworkPath: '',
    canEditMetadata: false,
    sections: {
      releases: false,
      sponsors: false,
      deployments: false,
      packages: false,
      usedBy: false,
      contributors: false,
      languages: false,
      cta: false,
      suggestedWorkflows: false,
    },
    repo: {
      ownerAvatarUrl: repo.ownerAvatar,
      isPrivate: repo.private,
      isTemplate: false,
      isFork: repo.isFork,
      isMirror: false,
      isArchived: false,
      visibilityLabel: repo.private ? 'Private' : 'Public',
    },
    star: {viewerHasStarred: false, canStar: false},
    fork: {canFork: false},
    pin: {canPin: false, isPinned: false, isOrgOwned: repo.isOrgOwned, pinItemsRemaining: 0},
    watch: {
      canWatch: false,
      watchData: {
        repositoryId: `${repo.databaseId ?? ''}`,
        repositoryName: `${repo.ownerLogin}/${repo.name}`,
        watchersCount: 0,
        subscriptionType: 'none',
        subscribableThreadTypes: [],
        repositoryLabels: [],
        showLabelSubscriptions: false,
        subscribedLabelIds: [],
      },
    },
    viewer: {isLoggedIn: !!currentUser, isSiteAdmin: !!currentUser?.isStaff, emuContributionBlocked: false},
    showSponsorButton: false,
    showTemplateButton: false,
  }
}

export function CodeViewEmptyLayout({data}: {data: LayoutPayload}) {
  const {repo} = data
  // TODO: show the global site footer on the empty repo state. The parent
  // `_codeview_layout` route in ui-service sets `staticData: {hideFooter: true}`
  // (see packages/ui-service/src/routes/code-view/layout.tsx), and that's
  // resolved statically across all matches in GlobalNavLayout — so there's no
  // per-render way to opt back in from here today. Needs either a runtime
  // override (e.g. a context the nav layout reads) or restructuring of the
  // route's static data before the footer can render on this page.
  // const {data: sidebarAbout, queryKey: sidebarAboutQueryKey} = useRouteQuery(codeViewLayoutRoute, 'sidebarAbout')
  const headerData = buildMockHeaderData(data)

  return (
    <SharedPageLayout narrow={false}>
      <SplitPageLayout className="container-xl">
        <SplitPageLayout.Header padding="none" divider="none" className="tmp-px-3">
          <CodeViewHeader {...headerData} />
        </SplitPageLayout.Header>
        <SplitPageLayout.Content as="div" padding="condensed" width="xlarge">
          <OnboardingPrompts repo={repo} />
          {repo.currentUserCanPush ? <QuickSetupBox repo={repo} /> : <Nothing />}
        </SplitPageLayout.Content>
      </SplitPageLayout>
    </SharedPageLayout>
  )
}
